import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronLeftLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {t} from 'i18next';
import type {EditorState} from 'lexical';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';

import type {ParsedChoice, ParsedQuestion} from './clarificationQuestions';
import {splitChoicesForUi} from './clarificationQuestions';
import {convertEditorJsonToMarkdown} from './WorkstreamAICoachTab';
import type {ClarificationAnswersDisplay} from './workstreamContextEngineChatApi';
import {formatClarificationUserMessage} from './workstreamContextEngineChatApi';

const OTHER_KEY = '__other__';
/** Wire payload for skipped questions so the model sees every index (avoids re-asking). */
const SKIPPED_ANSWER_PAYLOAD = 'skip';

function markdownFromEditorState(state: EditorState): string {
  try {
    return convertEditorJsonToMarkdown(JSON.stringify(state.toJSON())) ?? '';
  } catch {
    return '';
  }
}

export const WorkstreamClarificationMessage = ({
  intro,
  questions,
  onSubmit,
  onCancel,
  disabled,
  embeddedInComposer,
  includeOptionalStep = true,
}: {
  intro: string;
  questions: ParsedQuestion[];
  onSubmit: (composedUserMessage: string, display: ClarificationAnswersDisplay) => void;
  /** Close the wizard without submitting; parent typically restores freeform chat. */
  onCancel?: () => void;
  disabled?: boolean;
  /** When true, panel sits inside the composer card (no outer frame). */
  embeddedInComposer?: boolean;
  /** When false, skip the final “anything else?” step (e.g. after the first clarification round in a chat). */
  includeOptionalStep?: boolean;
}) => {
  const nQuestions = questions.length;
  const hasOptionalStep = includeOptionalStep;
  const totalSteps = nQuestions + (hasOptionalStep ? 1 : 0);

  const [step, setStep] = useState(0);
  const [choicePick, setChoicePick] = useState<Record<number, string>>({});
  const [freeformMd, setFreeformMd] = useState<Record<number, string>>({});
  const [otherMd, setOtherMd] = useState<Record<number, string>>({});
  const [optionalMd, setOptionalMd] = useState('');
  const [skippedByQuestionIndex, setSkippedByQuestionIndex] = useState<Record<number, true>>({});

  const freeformInputRef = useRef<{getEditorState: () => EditorState} | null>(null);
  const otherInputRef = useRef<{getEditorState: () => EditorState} | null>(null);
  const optionalInputRef = useRef<{getEditorState: () => EditorState} | null>(null);
  /** Same-tick skip for the final question; React state may not flush before `finishAll`. */
  const pendingSkippedQuestionIndexRef = useRef<number | null>(null);

  const questionsIdentity = useMemo(
    () =>
      questions
        .map(
          (x) =>
            `${x.questionId ?? x.index}\t${x.prompt}\t${x.choices.map((c) => `${c.key}:${c.label}`).join(';')}`
        )
        .join('\n---\n'),
    [questions]
  );

  useEffect(() => {
    setStep(0);
    setChoicePick({});
    setFreeformMd({});
    setOtherMd({});
    setOptionalMd('');
    setSkippedByQuestionIndex({});
  }, [questionsIdentity]);

  const clearQuestionSkip = useCallback((i: number) => {
    setSkippedByQuestionIndex((prev) => {
      if (!(i in prev)) return prev;
      const next = {...prev};
      delete next[i];
      return next;
    });
  }, []);

  const isOptionalStep = hasOptionalStep && step >= nQuestions;
  const q = !isOptionalStep ? questions[step] : undefined;
  const qIdx = step;

  const currentAnswered = useMemo(() => {
    if (isOptionalStep) return true;
    if (!q) return false;
    if (q.choices.length > 0) {
      const {primary, other} = splitChoicesForUi(q.choices);
      const otherText = (otherMd[qIdx] ?? '').trim();
      if (otherText) return true;
      const pick = (choicePick[qIdx] ?? '').trim();
      if (!pick) return false;
      if (other && pick === other.key) return false;
      if (!other && pick === OTHER_KEY) return Boolean((otherMd[qIdx] ?? '').trim());
      return primary.some((p) => p.key === pick);
    }
    return Boolean((freeformMd[qIdx] ?? '').trim());
  }, [isOptionalStep, q, qIdx, choicePick, freeformMd, otherMd]);

  const canGoPrev = step > 0;
  const canGoNext = step < totalSteps - 1;

  const goPrev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const goNext = useCallback(() => {
    setStep((s) => Math.min(totalSteps - 1, s + 1));
  }, [totalSteps]);

  /** Payload only — line key is the question index (matches assistant "1.", "2."). */
  const buildAnswerPayload = useCallback(
    (i: number): string | null => {
      if (skippedByQuestionIndex[i] || pendingSkippedQuestionIndexRef.current === i) {
        return SKIPPED_ANSWER_PAYLOAD;
      }
      const question = questions[i];
      if (question.choices.length > 0) {
        const {other} = splitChoicesForUi(question.choices);
        const ot = (otherMd[i] ?? '').trim();
        if (ot) {
          return other ? `(${other.key}) ${ot}` : `(OTHER) ${ot}`;
        }
        const pick = (choicePick[i] ?? '').trim();
        if (!pick) return null;
        const opt = question.choices.find((c) => c.key === pick);
        return opt ? `(${pick}) ${opt.label}` : null;
      }
      const ff = (freeformMd[i] ?? '').trim();
      return ff || null;
    },
    [questions, choicePick, freeformMd, otherMd, skippedByQuestionIndex]
  );

  const finishAll = useCallback(
    (optionalOverride?: string) => {
      try {
        const answers = questions.map((question, i) => {
          const payload = buildAnswerPayload(i) ?? SKIPPED_ANSWER_PAYLOAD;
          return {index: question.index, payload};
        });
        const opt = (optionalOverride !== undefined ? optionalOverride : optionalMd).trim();
        const pairs: ClarificationAnswersDisplay['pairs'] = questions.map((question, i) => {
          const payload = buildAnswerPayload(i) ?? SKIPPED_ANSWER_PAYLOAD;
          return {
            prompt: question.prompt,
            answer: payload,
            questionIndex: question.index,
          };
        });
        const display: ClarificationAnswersDisplay = {
          ...(intro.trim() ? {intro: intro.trim()} : {}),
          pairs,
          ...(opt ? {optionalNote: opt} : {}),
        };
        onSubmit(
          formatClarificationUserMessage({
            intro: intro.trim() || undefined,
            answers,
            optionalNote: opt || undefined,
          }),
          display
        );
      } finally {
        pendingSkippedQuestionIndexRef.current = null;
      }
    },
    [intro, questions, buildAnswerPayload, optionalMd, onSubmit]
  );

  const advanceOrComplete = useCallback(() => {
    if (step < totalSteps - 1) {
      setStep((s) => s + 1);
    } else {
      finishAll();
    }
  }, [step, totalSteps, finishAll]);

  const handleSkip = useCallback(() => {
    if (disabled) return;
    if (isOptionalStep) {
      finishAll('');
      return;
    }
    if (!q) return;
    setSkippedByQuestionIndex((prev) => ({...prev, [qIdx]: true}));
    if (q.choices.length > 0) {
      setChoicePick((prev) => ({...prev, [qIdx]: ''}));
      setOtherMd((prev) => ({...prev, [qIdx]: ''}));
    } else {
      setFreeformMd((prev) => ({...prev, [qIdx]: ''}));
    }
    if (step < totalSteps - 1) {
      setStep((s) => s + 1);
    } else {
      pendingSkippedQuestionIndexRef.current = qIdx;
      finishAll();
    }
  }, [disabled, isOptionalStep, q, qIdx, step, totalSteps, finishAll]);

  const handleContinue = useCallback(() => {
    if (disabled) return;
    if (isOptionalStep) {
      const st = optionalInputRef.current?.getEditorState?.();
      const md = st ? markdownFromEditorState(st) : '';
      finishAll(md);
      return;
    }
    if (!currentAnswered) return;
    advanceOrComplete();
  }, [disabled, isOptionalStep, currentAnswered, finishAll, advanceOrComplete]);

  const onFreeformChange = useCallback(
    (i: number, state: EditorState) => {
      const md = markdownFromEditorState(state);
      if (md.trim()) clearQuestionSkip(i);
      setFreeformMd((prev) => ({...prev, [i]: md}));
    },
    [clearQuestionSkip]
  );

  const onOtherChange = useCallback(
    (i: number, state: EditorState) => {
      const md = markdownFromEditorState(state);
      if (md.trim()) clearQuestionSkip(i);
      setOtherMd((prev) => ({...prev, [i]: md}));
      const question = questions[i];
      if (question?.choices.length) {
        const {other} = splitChoicesForUi(question.choices);
        if (other && md.trim()) {
          setChoicePick((prev) => ({...prev, [i]: other.key}));
        }
      }
    },
    [questions, clearQuestionSkip]
  );

  const onOptionalChange = useCallback((state: EditorState) => {
    setOptionalMd(markdownFromEditorState(state));
  }, []);

  const renderChoiceQuestion = (question: ParsedQuestion, idx: number) => {
    const {primary, other} = splitChoicesForUi(question.choices);
    const pick = choicePick[idx] ?? '';

    const selectPrimary = (c: ParsedChoice) => {
      clearQuestionSkip(idx);
      setChoicePick((prev) => ({...prev, [idx]: c.key}));
      if (other) {
        setOtherMd((prev) => ({...prev, [idx]: ''}));
      }
    };

    const showOtherField = Boolean(other);

    const submitOtherAndAdvance = () => {
      if (disabled) return;
      const st = otherInputRef.current?.getEditorState?.();
      const md = st ? markdownFromEditorState(st) : '';
      if (!md.trim()) return;
      const {other: o} = splitChoicesForUi(question.choices);
      flushSync(() => {
        setOtherMd((prev) => ({...prev, [idx]: md}));
        if (o) setChoicePick((prev) => ({...prev, [idx]: o.key}));
      });
      if (step < totalSteps - 1) setStep((s) => s + 1);
      else finishAll();
    };

    const submitLegacyOtherAndAdvance = () => {
      if (disabled) return;
      const st = otherInputRef.current?.getEditorState?.();
      const md = st ? markdownFromEditorState(st) : '';
      if (!md.trim()) return;
      flushSync(() => {
        setOtherMd((prev) => ({...prev, [idx]: md}));
        setChoicePick((prev) => ({...prev, [idx]: OTHER_KEY}));
      });
      if (step < totalSteps - 1) setStep((s) => s + 1);
      else finishAll();
    };

    return (
      <Stack display="flex" gap="8" style={{width: '100%'}}>
        <Stack display="flex" gap="6" style={{width: '100%'}}>
          {primary.map((c) => {
            const selected = pick === c.key;
            return (
              <Button
                key={c.key}
                size="small"
                variant={selected ? 'primary' : 'outline'}
                disabled={disabled}
                style={{width: '100%', justifyContent: 'flex-start'}}
                onClick={() => selectPrimary(c)}
              >
                <Box display="flex" alignItems="flex-start" style={{gap: 8, textAlign: 'left'}}>
                  <Text size="small" isBold style={{marginTop: 1}}>
                    {c.key}
                  </Text>
                  <Text size="small">{c.label}</Text>
                </Box>
              </Button>
            );
          })}
        </Stack>
        {showOtherField ? (
          <Box>
            <Box>
              <Text size="xsmall" color="subtle" style={{marginBottom: 6, display: 'block'}}>
                {other?.label ?? t('workstream_chat_choice_other')}
              </Text>
            </Box>
            <Box style={{width: '100%'}}>
              <RichTextArea
                ref={otherInputRef}
                key={`other-${idx}-${questionsIdentity}`}
                value={otherMd[idx] ?? ''}
                disableBorder
                paddingX="0"
                editable={!disabled}
                textColor="inherit"
                textSize="small"
                disableTables
                hideToolbar
                minHeight={56}
                focusNextOnTabAtBlockEnd
                placeholder={t('start_writing_placeholder')}
                onEnterSubmit={submitOtherAndAdvance}
                onChange={(state) => onOtherChange(idx, state)}
              />
            </Box>
          </Box>
        ) : (
          <Stack display="flex" gap="6" style={{width: '100%'}}>
            <Button
              size="small"
              variant={pick === OTHER_KEY ? 'primary' : 'outline'}
              disabled={disabled}
              style={{width: '100%', justifyContent: 'flex-start'}}
              onClick={() => {
                clearQuestionSkip(idx);
                setChoicePick((prev) => ({...prev, [idx]: OTHER_KEY}));
              }}
            >
              <Text size="small">{t('workstream_chat_choice_other')}</Text>
            </Button>
            {pick === OTHER_KEY ? (
              <Box style={{width: '100%'}}>
                <RichTextArea
                  ref={otherInputRef}
                  key={`legacy-other-${idx}-${questionsIdentity}`}
                  value={otherMd[idx] ?? ''}
                  disableBorder
                  paddingX="0"
                  editable={!disabled}
                  textColor="inherit"
                  textSize="small"
                  disableTables
                  hideToolbar
                  minHeight={56}
                  focusNextOnTabAtBlockEnd
                  placeholder={t('start_writing_placeholder')}
                  onEnterSubmit={submitLegacyOtherAndAdvance}
                  onChange={(state) => onOtherChange(idx, state)}
                />
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>
    );
  };

  const freeformRefStep = `ff-${qIdx}-${questionsIdentity}`;

  return (
    <Box
      padding={embeddedInComposer ? '12' : '12'}
      paddingBottom={embeddedInComposer ? '8' : '12'}
      style={
        embeddedInComposer
          ? {maxWidth: '100%', flexGrow: 1}
          : {
              maxWidth: '100%',
              flexGrow: 1,
              borderRadius: 'var(--dig-border-radius__medium)',
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'var(--dig-color__border__subtle)',
              backgroundColor: 'var(--dig-color__background__base)',
              boxShadow: 'var(--dig-utilities__boxshadow__floating)',
            }
      }
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="12">
        <Text size="small" isBold>
          {t('workstream_chat_questions_panel_title')}
        </Text>
        <Box display="flex" alignItems="center" style={{gap: 2}}>
          <IconButton
            size="small"
            variant="borderless"
            disabled={disabled || !canGoPrev}
            onClick={goPrev}
            aria-label={t('workstream_chat_questions_prev')}
          >
            <UIIcon src={ChevronLeftLine} />
          </IconButton>
          <Text size="xsmall" color="subtle">
            {t('workstream_chat_question_n_of_m', {n: step + 1, m: totalSteps})}
          </Text>

          <IconButton
            size="small"
            variant="borderless"
            disabled={disabled || !canGoNext}
            onClick={goNext}
            aria-label={t('workstream_chat_questions_next')}
          >
            <UIIcon src={ChevronRightLine} />
          </IconButton>
        </Box>
      </Box>

      {isOptionalStep ? (
        <Box marginBottom="8" display="flex" flexDirection="column" style={{gap: 8}}>
          <Box>
            <Text size="small" style={{whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: 8}}>
              {t('workstream_chat_optional_question_prompt')}
            </Text>
          </Box>
          <Box style={{width: '100%'}}>
            <RichTextArea
              ref={optionalInputRef}
              key={`optional-${questionsIdentity}`}
              value={optionalMd}
              disableBorder
              editable={!disabled}
              textColor="inherit"
              textSize="small"
              paddingX="0"
              disableTables
              hideToolbar
              minHeight={72}
              focusNextOnTabAtBlockEnd
              placeholder={t('workstream_chat_optional_details_placeholder')}
              onEnterSubmit={() => {
                if (disabled) return;
                const st = optionalInputRef.current?.getEditorState?.();
                const md = st ? markdownFromEditorState(st) : '';
                finishAll(md);
              }}
              onChange={(state) => onOptionalChange(state)}
            />
          </Box>
        </Box>
      ) : q ? (
        <Box marginBottom="8" display="flex" flexDirection="column" style={{gap: 8}}>
          <Box style={{width: '100%', marginBottom: 12}}>
            <RichTextArea
              key={`clarify-prompt-${qIdx}-${questionsIdentity}`}
              value={q.prompt}
              paddingX="0"
              editable={false}
              autoFocus={false}
              hideToolbar
              disableBorder
              disableTables
              textColor="inherit"
              textSize="small"
              minHeight={48}
              links
              clickLinks
            />
          </Box>
          {q.choices.length > 0 ? (
            renderChoiceQuestion(q, qIdx)
          ) : (
            <Box style={{width: '100%'}}>
              <RichTextArea
                ref={freeformInputRef}
                key={freeformRefStep}
                value={freeformMd[qIdx] ?? ''}
                disableBorder
                editable={!disabled}
                textColor="inherit"
                textSize="small"
                paddingX="0"
                disableTables
                hideToolbar
                minHeight={72}
                focusNextOnTabAtBlockEnd
                placeholder={t('start_writing_placeholder')}
                onEnterSubmit={() => {
                  if (disabled) return;
                  const st = freeformInputRef.current?.getEditorState?.();
                  const md = st ? markdownFromEditorState(st) : '';
                  if (!md.trim()) return;
                  flushSync(() => setFreeformMd((prev) => ({...prev, [qIdx]: md})));
                  if (step < totalSteps - 1) setStep((s) => s + 1);
                  else finishAll();
                }}
                onChange={(state) => onFreeformChange(qIdx, state)}
              />
            </Box>
          )}
        </Box>
      ) : null}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop="12"
        style={{gap: 16}}
      >
        {onCancel ? (
          <Button
            size="small"
            variant="transparent"
            disabled={disabled}
            onClick={() => !disabled && onCancel()}
          >
            <Text size="small" color="subtle">
              {t('workstream_chat_cancel_questions')}
            </Text>
          </Button>
        ) : (
          <Box />
        )}
        <Box display="flex" alignItems="center" style={{gap: 16}}>
          <Button
            size="small"
            variant="opacity"
            disabled={disabled}
            onClick={() => !disabled && handleSkip()}
          >
            {t('workstream_chat_skip_question')}
          </Button>
          <Button
            size="small"
            variant="primary"
            disabled={disabled || (!isOptionalStep && !currentAnswered)}
            onClick={handleContinue}
          >
            {isOptionalStep ? t('workstream_chat_submit_answers') : t('workstream_chat_continue')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
